import React, { useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvButton,
  SalvFieldHeader,
  SalvModal,
  SalvTextField,
  DatePicker,
} from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Dayjs } from 'dayjs';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useRerunPeriodicScenario } from './scenarioRerunActions';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  scenarioHandle: string;
  timestamp: Dayjs | null;
}

const validationSchema = Yup.object().shape({
  scenarioHandle: Yup.string().required('Scenario handle is required'),
  timestamp: Yup.mixed<Dayjs>().required('Timestamp is required'),
});

const RerunPeriodicScenarioModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useRerunPeriodicScenario();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { scenarioHandle: '', timestamp: null };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        scenarioHandle: values.scenarioHandle,
        timestamp: values.timestamp!.toDate(),
      });
      dispatch(showFeedback({ message: 'Periodic scenario rerun triggered successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Rerun Periodic Scenario</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <SalvTextField
                label='Scenario Handle'
                value={values.scenarioHandle}
                onChange={(value: string) => setFieldValue('scenarioHandle', value)}
                className='m-b-16'
              />
              <SalvFieldHeader label='Timestamp' className='m-b-4' />
              <DatePicker
                value={values.timestamp}
                onChange={(date: Dayjs | null) => setFieldValue('timestamp', date)}
                showTime
              />
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Rerun' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default RerunPeriodicScenarioModal;
